// src/components/notifications/NotificationBell.tsx
// Notification bell with dropdown panel showing "This App" and "Workspace" tabs
//
// Data flows through the CRM BFF (`/api/platform/notifications/inbox*` via
// `platformNotificationsApi`), NOT browser-direct to Central — so it works in
// RP/BFF mode where the browser holds no Central token. The server derives the
// workspace from the session, so these hooks take NO workspaceId. Central stays
// authoritative for unread count, list, grouping, pagination, and mark-read.
// Freshness is via bounded polling (PR-N1); the same-origin SSE proxy is a
// PR-N2 follow-up.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import type { CentralNotification, GroupedNotification } from '@levelup/central-notifications';
import {
  useGetPlatformGroupedInboxQuery,
  useGetPlatformInboxGroupQuery,
  useGetPlatformUnreadCountQuery,
  useMarkPlatformNotificationReadMutation,
  useMarkPlatformBulkReadMutation,
  useMarkPlatformGroupReadMutation,
} from '../../api/platformNotificationsApi';
import { APP_ID } from '../../config/centralAuth';
import { NOTIFICATION_POLL_INTERVAL_MS } from '../../config/notifications';
import { GroupedNotificationItem, SourceAppBadge } from './GroupedNotificationItem';

// ============================================================
// Icons
// ============================================================

const BellIcon = ({ className = '' }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);

const CheckIcon = ({ className = '' }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ExternalLinkIcon = ({ className = '' }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
);

// ============================================================
// Types
// ============================================================

type TabKey = 'thisApp' | 'workspace';

// ============================================================
// NotificationItem Component (for individual notifications)
// ============================================================

interface NotificationItemProps {
  notification: CentralNotification;
  onMarkRead: () => void;
  showAppIndicator?: boolean;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkRead,
  showAppIndicator = false,
}) => {
  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true });

  const handleClick = () => {
    // Mark as read if unread
    if (!notification.isRead) {
      onMarkRead();
    }

    // Navigate to action URL if present
    if (notification.actionUrl) {
      window.open(notification.actionUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className={`
        flex items-start gap-3 p-3 cursor-pointer transition-colors
        ${notification.isRead ? 'bg-white' : 'bg-blue-50'}
        hover:bg-gray-50
      `}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      {/* App indicator using enriched metadata */}
      <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center">
        {notification.appMetadata?.appIconUrl ? (
          <img
            src={notification.appMetadata.appIconUrl}
            alt={notification.appMetadata.appDisplayName}
            className="w-8 h-8 rounded"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <span className="text-xl">{getDefaultAppIcon(notification.appId)}</span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {showAppIndicator && (
          <SourceAppBadge
            appId={notification.appId}
            appMetadata={notification.appMetadata}
          />
        )}
        <p className={`text-sm ${notification.isRead ? 'text-gray-700' : 'text-gray-900 font-medium'}`}>
          {notification.title}
        </p>
        {notification.body && (
          <p className="text-xs text-gray-500 truncate">{notification.body}</p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-gray-400">{timeAgo}</span>
          {notification.actionLabel && (
            <span className="text-xs text-blue-600 flex items-center gap-0.5">
              {notification.actionLabel}
              <ExternalLinkIcon className="w-3 h-3" />
            </span>
          )}
        </div>
      </div>

      {/* Mark read button */}
      {!notification.isRead && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMarkRead();
          }}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-blue-600 rounded"
          title="Mark as read"
        >
          <CheckIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// Default app icon (fallback)
function getDefaultAppIcon(appId: string | null): string {
  if (!appId) return '🔔';
  switch (appId) {
    case 'crm':
      return '📊';
    case 'levelup-central':
      return '🏢';
    case 'levelup-docs':
      return '📄';
    case 'levelup-payments':
      return '💳';
    default:
      return '📱';
  }
}

// ============================================================
// GroupedNotificationList Component
// ============================================================

interface GroupedNotificationListProps {
  appId?: string;
  showAppIndicator?: boolean;
}

const GroupedNotificationList: React.FC<GroupedNotificationListProps> = ({
  appId,
  showAppIndicator = false,
}) => {
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

  // Workspace is derived server-side from the session — no workspaceId here.
  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useGetPlatformGroupedInboxQuery(
    { appId, limit: 20, grouped: true, enriched: true },
    { refetchOnMountOrArgChange: true, pollingInterval: NOTIFICATION_POLL_INTERVAL_MS }
  );

  // Fetch expanded group notifications
  const {
    data: groupDetailData,
    isLoading: isLoadingGroupDetail,
  } = useGetPlatformInboxGroupQuery(
    { correlationId: expandedGroupId || '' },
    { skip: !expandedGroupId }
  );

  const [markRead] = useMarkPlatformNotificationReadMutation();
  const [markBulkRead] = useMarkPlatformBulkReadMutation();
  const [markGroupRead] = useMarkPlatformGroupReadMutation();

  const items = data?.items ?? [];
  const hasMore = data?.hasMore ?? false;

  const handleMarkRead = useCallback(
    async (id: string) => {
      await markRead(id);
    },
    [markRead]
  );

  const handleMarkGroupRead = useCallback(
    async (correlationId: string) => {
      await markGroupRead({ correlationId });
    },
    [markGroupRead]
  );

  const handleMarkAllRead = useCallback(async () => {
    await markBulkRead({ appId });
  }, [markBulkRead, appId]);

  const handleNotificationClick = useCallback((notification: CentralNotification) => {
    if (!notification.isRead) {
      markRead(notification.id);
    }
    if (notification.actionUrl) {
      window.open(notification.actionUrl, '_blank', 'noopener,noreferrer');
    }
  }, [markRead]);

  const handleExpandGroup = useCallback((correlationId: string) => {
    setExpandedGroupId(correlationId);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (data?.nextCursor) {
      refetch();
    }
  }, [data?.nextCursor, refetch]);

  // Loading state
  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-gray-500 mt-2">Loading notifications...</p>
      </div>
    );
  }

  // Empty state
  if (items.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        <BellIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
        <p className="font-medium">No notifications</p>
        <p className="text-sm mt-1">
          {appId ? "You're all caught up in this app" : "You're all caught up across all apps"}
        </p>
      </div>
    );
  }

  // Calculate unread count
  const unreadCount = items.reduce((count, item) => {
    if (item.kind === 'group') {
      return count + (item.data.isRead ? 0 : 1);
    } else {
      return count + (item.data.isRead ? 0 : 1);
    }
  }, 0);

  return (
    <>
      {/* Mark all read button */}
      {unreadCount > 0 && (
        <div className="px-4 py-2 border-b border-gray-100 flex justify-end">
          <button
            onClick={handleMarkAllRead}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Mark all read
          </button>
        </div>
      )}

      {/* Notification items */}
      <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
        {items.map((item) => {
          if (item.kind === 'group') {
            const group = item.data as GroupedNotification;
            return (
              <GroupedNotificationItem
                key={group.correlationId}
                group={group}
                onMarkGroupRead={() => handleMarkGroupRead(group.correlationId)}
                onNotificationClick={handleNotificationClick}
                expandedNotifications={
                  expandedGroupId === group.correlationId
                    ? groupDetailData?.notifications
                    : undefined
                }
                isLoadingExpanded={
                  expandedGroupId === group.correlationId && isLoadingGroupDetail
                }
                onExpand={() => handleExpandGroup(group.correlationId)}
              />
            );
          } else {
            const notification = item.data as CentralNotification;
            return (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkRead={() => handleMarkRead(notification.id)}
                showAppIndicator={showAppIndicator}
              />
            );
          }
        })}
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="border-t border-gray-100 p-2">
          <button
            onClick={handleLoadMore}
            disabled={isFetching}
            className="w-full py-2 text-sm text-center text-blue-600 hover:bg-gray-50 rounded disabled:opacity-50"
          >
            {isFetching ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </>
  );
};

// ============================================================
// Main NotificationBell Component
// ============================================================

export const NotificationBell: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('thisApp');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Total unread count (workspace-wide) for the badge. Bounded polling for
  // freshness (PR-N1); the same-origin SSE proxy is a PR-N2 follow-up.
  const { data: unreadData } = useGetPlatformUnreadCountQuery(undefined, {
    pollingInterval: NOTIFICATION_POLL_INTERVAL_MS,
  });

  // Central's canonical workspace-wide unread field is `total` (not `unreadCount`).
  const unreadCount = unreadData?.total ?? 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'thisApp', label: 'This App' },
    { key: 'workspace', label: 'Workspace' },
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Notifications"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <BellIcon className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-xs font-bold text-white bg-red-500 rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50"
          role="dialog"
          aria-label="Notifications"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  flex-1 px-4 py-2.5 text-sm font-medium transition-colors
                  ${activeTab === tab.key
                    ? 'text-blue-600 border-b-2 border-blue-600 -mb-px'
                    : 'text-gray-500 hover:text-gray-700'
                  }
                `}
                role="tab"
                aria-selected={activeTab === tab.key}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div role="tabpanel">
            {activeTab === 'thisApp' ? (
              <GroupedNotificationList
                appId={APP_ID}
                showAppIndicator={false}
              />
            ) : (
              <GroupedNotificationList
                showAppIndicator={true}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
